import React, { useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import FlowField from '@/components/Landing/FlowField';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import EnterpriseHero from '@/components/Enterprise/EnterpriseHero';
import EnterpriseServices from '@/components/Enterprise/EnterpriseServices';
import EnterpriseProcess from '@/components/Enterprise/EnterpriseProcess';
import EnterpriseWhy from '@/components/Enterprise/EnterpriseWhy';
import EnterpriseIndustries from '@/components/Enterprise/EnterpriseIndustries';
import EnterpriseClosing from '@/components/Enterprise/EnterpriseClosing';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import { CONTACT_EMAIL } from '@/constants/enterpriseContent';

/**
 * /enterprise, art-directed with the same kit as the landing and /about pages.
 *
 * The argument runs in order: who you would be hiring, what the work is, how it
 * runs, why us, where it has applied — alternating between the page surface and
 * a recessed band so each movement reads as its own.
 */
export default function EnterprisePage() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();

  useLandingStyles();

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  }), [colors]);

  return (
    <View style={containerStyle}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Enterprise AI Solutions"
        description="Custom-built AI solutions for enterprise businesses. Our team of AI experts designs, builds, and deploys tailored AI systems for your organization. Powered by Gab AI."
        canonical="/enterprise"
        keywords="enterprise AI, custom AI solutions, AI consulting, AI development team, custom AI models, enterprise chatbot, AI integration, Gab AI enterprise"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Gab AI Enterprise Solutions',
          description: 'Custom-built AI solutions for enterprise businesses powered by Gab AI.',
          url: 'https://gab.ai/enterprise',
          mainEntity: {
            '@type': 'Service',
            name: 'Gab AI Enterprise',
            description: 'Custom AI development, integration, and consulting services for enterprise businesses.',
            serviceType: 'Custom AI development and consulting',
            areaServed: 'US',
            provider: {
              '@type': 'Organization',
              name: 'Gab AI Inc.',
              url: 'https://gab.ai',
              email: CONTACT_EMAIL,
            },
          },
        }}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole="main"
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <EnterpriseHero topInset={insets.top} />
        <EnterpriseServices />
        <EnterpriseProcess />
        <EnterpriseWhy />
        <EnterpriseIndustries />
        <EnterpriseClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
